/* LITE-tier stills — pure SVG "renders" of each cinematic scene.
   Each function returns a data URL that CinematicShell can drop into <img src>.
   Because they're SVG, they weigh <5KB per scene and stay crisp at any DPI.

   Doctrine reminders enforced here:
   - Emissives are ONLY Glory Red or Union Blue.
   - The 13-star ring appears ONLY on the Standard Seal still.
   - No eagles, no waving flag meshes.
   - Every still has the same war-room backdrop treatment so LITE feels cohesive.
*/

const BG = "#0B0E14";
const METAL = "#1A1F2A";
const PARCHMENT = "#EDE8DC";
const RED = "#B22234";
const BLUE = "#1F3A93";

// Reusable background — dark radial + faint star field.
const BG_DEFS = `
  <defs>
    <radialGradient id='bg' cx='50%' cy='45%' r='60%'>
      <stop offset='0%' stop-color='${BLUE}' stop-opacity='0.22'/>
      <stop offset='55%' stop-color='${BG}' stop-opacity='1'/>
    </radialGradient>
    <radialGradient id='bgred' cx='50%' cy='60%' r='55%'>
      <stop offset='0%' stop-color='${RED}' stop-opacity='0.14'/>
      <stop offset='60%' stop-color='transparent'/>
    </radialGradient>
    <linearGradient id='metal' x1='0' y1='0' x2='0' y2='1'>
      <stop offset='0%' stop-color='#252c3a'/>
      <stop offset='100%' stop-color='#0f1319'/>
    </linearGradient>
    <filter id='glow' x='-40%' y='-40%' width='180%' height='180%'>
      <feGaussianBlur stdDeviation='6'/>
      <feMerge><feMergeNode/><feMergeNode in='SourceGraphic'/></feMerge>
    </filter>
    <filter id='softglow' x='-40%' y='-40%' width='180%' height='180%'>
      <feGaussianBlur stdDeviation='3.5'/>
      <feMerge><feMergeNode/><feMergeNode in='SourceGraphic'/></feMerge>
    </filter>
  </defs>
  <rect width='1600' height='900' fill='${BG}'/>
  <rect width='1600' height='900' fill='url(#bg)'/>
  <rect width='1600' height='900' fill='url(#bgred)'/>
  <!-- Sparse star field -->
  <g fill='${PARCHMENT}' opacity='0.75'>
    <circle cx='120' cy='140' r='1.2'/>
    <circle cx='260' cy='260' r='1'/>
    <circle cx='420' cy='90' r='1.4'/>
    <circle cx='560' cy='210' r='1'/>
    <circle cx='700' cy='60' r='1.1'/>
    <circle cx='860' cy='170' r='1.2'/>
    <circle cx='1020' cy='120' r='1'/>
    <circle cx='1180' cy='240' r='1.3'/>
    <circle cx='1340' cy='90' r='1.1'/>
    <circle cx='1480' cy='210' r='1'/>
    <circle cx='80'  cy='650' r='1'/>
    <circle cx='240' cy='770' r='1.2'/>
    <circle cx='400' cy='830' r='1'/>
    <circle cx='560' cy='700' r='1.1'/>
    <circle cx='720' cy='840' r='1'/>
    <circle cx='920' cy='780' r='1.3'/>
    <circle cx='1080' cy='720' r='1'/>
    <circle cx='1260' cy='830' r='1.1'/>
    <circle cx='1420' cy='760' r='1'/>
  </g>
`;

const svgToDataUrl = (svg) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(svg.replace(/\s+/g, " ").trim())}`;

/** Five-point star path centered at (cx, cy) with outer radius r. */
function starPath(cx, cy, r) {
  const pts = [];
  for (let i = 0; i < 10; i += 1) {
    const rad = i % 2 === 0 ? r : r * 0.42;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(cx + Math.cos(a) * rad).toFixed(1)},${(cy + Math.sin(a) * rad).toFixed(1)}`);
  }
  return `M${pts.join(" L ")} Z`;
}

// ------------------------- Home Arsenal (Act 1) -------------------------
export const HomeArsenalStill = svgToDataUrl(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
  ${BG_DEFS}
  <!-- Machined DT6 monolith centered -->
  <g transform='translate(800 460)'>
    <rect x='-70' y='-180' width='140' height='360' rx='4' fill='url(#metal)' stroke='${METAL}' stroke-width='1'/>
    <!-- Emissive '6' face -->
    <rect x='-45' y='-90' width='90' height='170' fill='${RED}' filter='url(#glow)' opacity='0.92'/>
    <!-- Union Blue base rim -->
    <rect x='-78' y='170' width='156' height='10' fill='${BLUE}' filter='url(#softglow)'/>
  </g>
  <!-- Horizon dot ring -->
  <g fill='${PARCHMENT}' opacity='0.35'>
    ${Array.from({ length: 40 }).map((_, i) => {
      const t = (i / 40) * Math.PI * 2;
      const x = 800 + Math.cos(t) * 360;
      const y = 780 + Math.sin(t) * 40;
      return `<circle cx='${x.toFixed(0)}' cy='${y.toFixed(0)}' r='1.5'/>`;
    }).join("")}
  </g>
</svg>
`);

// ------------------------- Home Flywheel (Act 2/3) ----------------------
export const HomeFlywheelStill = svgToDataUrl(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
  ${BG_DEFS}
  <g transform='translate(800 460)'>
    <!-- 6 module cores in orbital -->
    ${Array.from({ length: 6 }).map((_, i) => {
      const t = (i / 6) * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(t) * 240;
      const y = Math.sin(t) * 100;
      const color = i % 2 === 0 ? RED : BLUE;
      return `
        <g transform='translate(${x.toFixed(0)} ${y.toFixed(0)})'>
          <circle r='34' fill='url(#metal)' stroke='${color}' stroke-width='1.5' opacity='0.95'/>
          <circle r='18' fill='${color}' opacity='0.6' filter='url(#softglow)'/>
        </g>`;
    }).join("")}
    <!-- Light conduits between consecutive nodes -->
    ${Array.from({ length: 6 }).map((_, i) => {
      const t1 = (i / 6) * Math.PI * 2 - Math.PI / 2;
      const t2 = ((i + 1) / 6) * Math.PI * 2 - Math.PI / 2;
      const x1 = Math.cos(t1) * 240, y1 = Math.sin(t1) * 100;
      const x2 = Math.cos(t2) * 240, y2 = Math.sin(t2) * 100;
      return `<line x1='${x1.toFixed(0)}' y1='${y1.toFixed(0)}' x2='${x2.toFixed(0)}' y2='${y2.toFixed(0)}' stroke='${RED}' stroke-width='2' opacity='0.75' filter='url(#softglow)'/>`;
    }).join("")}
  </g>
</svg>
`);

// ------------------------- Home Seal (Act 4) ----------------------------
function sealSvg(scale = 1) {
  const cx = 800, cy = 460;
  const r = 220 * scale;
  const starR = 12 * scale;
  return `
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
    ${BG_DEFS}
    <g>
      <!-- Engraved glass disc -->
      <circle cx='${cx}' cy='${cy}' r='${r - 10}' fill='${PARCHMENT}' opacity='0.06'/>
      <!-- Machined outer ring -->
      <circle cx='${cx}' cy='${cy}' r='${r}' fill='none' stroke='${METAL}' stroke-width='6'/>
      <!-- Union Blue inner ring, softly glowing -->
      <circle cx='${cx}' cy='${cy}' r='${r * 0.72}' fill='none' stroke='${BLUE}' stroke-width='2' filter='url(#softglow)' opacity='0.9'/>
      <!-- 13 stars — the ONLY place this ring appears -->
      ${Array.from({ length: 13 }).map((_, i) => {
        const t = (i / 13) * Math.PI * 2 - Math.PI / 2;
        const sx = cx + Math.cos(t) * (r * 0.86);
        const sy = cy + Math.sin(t) * (r * 0.86);
        return `<path d='${starPath(sx, sy, starR)}' fill='${RED}' filter='url(#softglow)' opacity='0.98'/>`;
      }).join("")}
      <!-- Center five-point star -->
      <path d='${starPath(cx, cy, starR * 3.2)}' fill='${RED}' filter='url(#glow)'/>
    </g>
  </svg>`;
}
export const HomeSealStill = svgToDataUrl(sealSvg(1));
export const StandardSealStill = svgToDataUrl(sealSvg(1.15));

// ------------------------- Platform Machine -----------------------------
export const PlatformMachineStill = svgToDataUrl(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
  ${BG_DEFS}
  <!-- Scattered debris (fragmentation) on the left -->
  <g opacity='0.6' stroke='${PARCHMENT}' stroke-width='1' fill='none'>
    <rect x='120' y='320' width='40' height='40' transform='rotate(15 140 340)'/>
    <rect x='200' y='420' width='30' height='30' transform='rotate(-8 215 435)'/>
    <rect x='260' y='260' width='28' height='28' transform='rotate(22 274 274)'/>
    <rect x='90'  y='500' width='22' height='22' transform='rotate(-15 101 511)'/>
    <rect x='320' y='520' width='32' height='32' transform='rotate(5 336 536)'/>
  </g>
  <!-- Assembled engine on the right -->
  <g transform='translate(1000 460)'>
    ${Array.from({ length: 6 }).map((_, i) => {
      const t = (i / 6) * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(t) * 200;
      const y = Math.sin(t) * 90;
      const color = i % 2 === 0 ? RED : BLUE;
      return `
        <g transform='translate(${x.toFixed(0)} ${y.toFixed(0)})'>
          <circle r='30' fill='url(#metal)' stroke='${color}' stroke-width='1.5'/>
          <circle r='16' fill='${color}' opacity='0.6' filter='url(#softglow)'/>
        </g>`;
    }).join("")}
    ${Array.from({ length: 6 }).map((_, i) => {
      const t1 = (i / 6) * Math.PI * 2 - Math.PI / 2;
      const t2 = ((i + 1) / 6) * Math.PI * 2 - Math.PI / 2;
      const x1 = Math.cos(t1) * 200, y1 = Math.sin(t1) * 90;
      const x2 = Math.cos(t2) * 200, y2 = Math.sin(t2) * 90;
      return `<line x1='${x1.toFixed(0)}' y1='${y1.toFixed(0)}' x2='${x2.toFixed(0)}' y2='${y2.toFixed(0)}' stroke='${RED}' stroke-width='2' opacity='0.7' filter='url(#softglow)'/>`;
    }).join("")}
  </g>
</svg>
`);

// ------------------------- Module Inspection (D1-D6) --------------------
export function moduleStill(code = "D1") {
  const cfg = {
    D1: { color: RED,  shape: "obelisk" },
    D2: { color: BLUE, shape: "ring"    },
    D3: { color: RED,  shape: "lens"    },
    D4: { color: BLUE, shape: "reticle" },
    D5: { color: RED,  shape: "lattice" },
    D6: { color: BLUE, shape: "dish"    },
  }[code] || { color: RED, shape: "obelisk" };
  const cx = 800, cy = 460;
  const shapeSvg = (() => {
    switch (cfg.shape) {
      case "ring":    return `<circle cx='${cx}' cy='${cy}' r='140' fill='none' stroke='${METAL}' stroke-width='16'/>
                              <circle cx='${cx}' cy='${cy}' r='140' fill='none' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>`;
      case "lens":    return `<path d='M${cx - 150} ${cy} A 150 150 0 0 1 ${cx + 150} ${cy} Z' fill='url(#metal)' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>`;
      case "reticle": return `
        <circle cx='${cx}' cy='${cy}' r='120' fill='none' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>
        <line x1='${cx - 160}' y1='${cy}' x2='${cx + 160}' y2='${cy}' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>
        <line x1='${cx}' y1='${cy - 160}' x2='${cx}' y2='${cy + 160}' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>`;
      case "lattice": return `
        <polygon points='${cx},${cy - 130} ${cx + 120},${cy - 30} ${cx + 90},${cy + 110} ${cx - 90},${cy + 110} ${cx - 120},${cy - 30}'
          fill='url(#metal)' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>`;
      case "dish":    return `
        <path d='M${cx - 160} ${cy + 80} L${cx + 160} ${cy + 80} L${cx + 110} ${cy - 60} L${cx - 110} ${cy - 60} Z'
          fill='url(#metal)' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>`;
      default:        return `<rect x='${cx - 70}' y='${cy - 160}' width='140' height='320' rx='4' fill='url(#metal)' stroke='${cfg.color}' stroke-width='2' filter='url(#softglow)'/>`;
    }
  })();
  return svgToDataUrl(`
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
      ${BG_DEFS}
      ${shapeSvg}
      <text x='${cx}' y='${cy + 260}' text-anchor='middle' font-family='JetBrains Mono, monospace' font-size='22' letter-spacing='6' fill='${PARCHMENT}' opacity='0.55'>${code} · INSPECTION BAY</text>
    </svg>
  `);
}

// ------------------------- Pricing Hangar -------------------------------
export const PricingHangarStill = svgToDataUrl(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
  ${BG_DEFS}
  <!-- 3 red plinths + 1 blue white-label plinth -->
  ${[
    { x: 340, cores: 1, color: RED,  glow: 0.7 },
    { x: 640, cores: 3, color: RED,  glow: 0.9 },
    { x: 940, cores: 6, color: RED,  glow: 1.1 },
    { x: 1300, cores: 2, color: BLUE, glow: 0.7 },
  ].map(({ x, cores, color, glow }) => `
    <g transform='translate(${x} 560)'>
      <ellipse cx='0' cy='0' rx='120' ry='16' fill='url(#metal)' stroke='${color}' stroke-width='2' opacity='${glow}' filter='url(#softglow)'/>
      ${Array.from({ length: cores }).map((_, i) => {
        const t = cores === 1 ? 0 : (i / cores) * Math.PI * 2;
        const r = cores === 1 ? 0 : 40;
        const cx = Math.cos(t) * r;
        const cy = -70 - Math.sin(i * 1.3) * 10;
        const cc = i % 2 === 0 ? RED : BLUE;
        return `<circle cx='${cx.toFixed(0)}' cy='${cy.toFixed(0)}' r='18' fill='url(#metal)' stroke='${cc}' stroke-width='1.5'/>
                <circle cx='${cx.toFixed(0)}' cy='${cy.toFixed(0)}' r='9' fill='${cc}' opacity='0.7' filter='url(#softglow)'/>`;
      }).join("")}
    </g>
  `).join("")}
</svg>
`);

// ------------------------- Partners Forge -------------------------------
export const PartnersForgeStill = svgToDataUrl(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
  ${BG_DEFS}
  <g transform='translate(800 460)'>
    <polygon points='0,-130 120,-30 90,110 -90,110 -120,-30'
      fill='url(#metal)' stroke='${RED}' stroke-width='2' filter='url(#softglow)'/>
    <ellipse cx='0' cy='-240' rx='90' ry='16' fill='none' stroke='${BLUE}' stroke-width='2' filter='url(#softglow)'/>
  </g>
</svg>
`);

// ------------------------- Company Parade -------------------------------
export const CompanyParadeStill = svgToDataUrl(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
  ${BG_DEFS}
  <g transform='translate(800 460)'>
    <rect x='-60' y='-160' width='120' height='320' rx='4' fill='url(#metal)' stroke='${METAL}' stroke-width='1'/>
    <rect x='-38' y='-70' width='76' height='140' fill='${RED}' filter='url(#softglow)' opacity='0.85'/>
    <rect x='-66' y='160' width='132' height='6' fill='${BLUE}' filter='url(#softglow)' opacity='0.8'/>
  </g>
</svg>
`);

// ------------------------- Audit Refinery -------------------------------
export const AuditRefineryStill = svgToDataUrl(`
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>
  ${BG_DEFS}
  <g transform='translate(800 460)'>
    <!-- Refining ring (D2, blue) -->
    <circle cx='0' cy='0' r='160' fill='none' stroke='${METAL}' stroke-width='18'/>
    <circle cx='0' cy='0' r='160' fill='none' stroke='${BLUE}' stroke-width='2' filter='url(#softglow)'/>
    <!-- Data slab passing through -->
    <rect x='-200' y='-20' width='60' height='40' fill='url(#metal)' stroke='${RED}' stroke-width='2' filter='url(#softglow)'/>
    <rect x='140'  y='-20' width='60' height='40' fill='url(#metal)' stroke='${BLUE}' stroke-width='2' filter='url(#softglow)'/>
  </g>
</svg>
`);
